import React, { useEffect, useRef } from 'react'
import { Highlighter, Typeahead } from 'react-bootstrap-typeahead'
import { WIDGET_FONTS } from './screenWidgetSettings'

const requested = new Set()

// Namespaced so previews can't pick up an admin-app font with the same name.
function previewFamily(font) {
  return font.id ? `'widget-preview-${font.id}', Arial, sans-serif` : 'Arial, sans-serif'
}

// Fetches the font's 400 weight (the same file the TV uses) on first request.
function loadPreviewFont(font) {
  if (!font.id || requested.has(font.id) || typeof FontFace === 'undefined') return
  requested.add(font.id)
  const url = `${process.env.PUBLIC_URL}/fonts/widgets/${font.id}/${font.id}-400.woff2`
  new FontFace(`widget-preview-${font.id}`, `url(${url})`)
    .load()
    .then(face => document.fonts.add(face))
    .catch(() => requested.delete(font.id))
}

// Show every font while the input still reads as the current selection, so opening
// the menu lists all fonts rather than just the ones matching the selected name.
function isShowingAll(text, selected) {
  return selected.length > 0 && text === selected[0].label
}

function filterBy(option, props) {
  return (
    isShowingAll(props.text, props.selected) ||
    option.label.toLowerCase().includes(props.text.toLowerCase())
  )
}

function WidgetFontPicker({ value, onChange, disabled, isInvalid }) {
  const ref = useRef()
  // Default stays unselected (the placeholder names it). Selecting it would break typing:
  // the typeahead clears the selection on input, which maps straight back to Default.
  const selected = value ? WIDGET_FONTS.filter(font => font.id === value) : []

  useEffect(() => {
    selected.forEach(loadPreviewFont)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Typeahead
      ref={ref}
      id='branding-font'
      disabled={disabled}
      options={WIDGET_FONTS}
      labelKey='label'
      filterBy={filterBy}
      selected={selected}
      placeholder='Default (Arial)'
      onFocus={() => ref.current?.getInput()?.select()}
      onMenuToggle={isOpen => isOpen && WIDGET_FONTS.forEach(loadPreviewFont)}
      onChange={next => onChange(next[0] ? next[0].id : '')}
      inputProps={{
        isInvalid,
        style: selected[0] ? { fontFamily: previewFamily(selected[0]) } : undefined,
      }}
      renderMenuItemChildren={(option, props) => (
        <span style={{ fontFamily: previewFamily(option), fontSize: '1.15rem' }}>
          {isShowingAll(props.text, selected) ? (
            option.label
          ) : (
            <Highlighter search={props.text}>{option.label}</Highlighter>
          )}
        </span>
      )}
    />
  )
}

export default WidgetFontPicker
